/** @file attachment.detach: removes the last attachment and everything it flattened into the puppet. */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import type { CommandDef } from '../apply/commandDef';
import { clipInverse } from '../apply/clipInverse';
import { puppetInverse } from '../apply/puppetInverse';
import { checkPuppet } from '../puppetDoc/checkPuppet';
import { readInput } from '../validate/readInput';
import { prefixOf } from './prefixOf';
import { removePrefixed } from './removePrefixed';

/**
 * `attachment.detach { id }` → `{}`: only the last attachment (prefixes never renumber); its `att<i>.` nodes,
 * drawables, sheets, clips and sockets go. Inverse: P, then `clip.import` of each removed clip.
 */
export const attachmentDetachCommand: CommandDef<'attachment.detach'> = {
  name: 'attachment.detach',
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => ({ id: readInput(input, path, ['id']).str('id') }),
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input, ctx) => {
    const p = draft.puppet;
    const list = p.attachments ?? [];
    const index = list.findIndex((a) => a.id === input.id);
    if (index < 0) throwError(ERROR_CODES.NOT_FOUND, { id: input.id }, { path: '/input/id', hint: `attachments: ${list.map((a) => a.id).join(', ')}` });
    if (index !== list.length - 1) throwError(ERROR_CODES.VALIDATION, { id: input.id }, { path: '/input/id', hint: 'detach the later attachments first' });
    const prefix = prefixOf(index);
    const inverse = [...puppetInverse(draft), ...Object.entries(draft.clips).filter(([c]) => c.startsWith(prefix)).map(([, c]) => clipInverse(c, false))];
    removePrefixed(draft, prefix);
    if (list.length === 1) delete p.attachments;
    else p.attachments = list.slice(0, index);
    checkPuppet(draft, ctx);
    return { result: {}, warnings: [], inverse };
  },
};
